/**
 * @file Wraps the app with the Amplify Authenticator and its custom UI.
 */
import type { ReactNode } from "react";
import { Amplify } from "aws-amplify";
import { Authenticator } from "@aws-amplify/ui-react";
import type { AuthenticatorProps } from "@aws-amplify/ui-react";

import { getConfig } from "@components/Config";
import {
  isInternalDomain,
  usePublicOrganization,
  PublicOrganizationContext,
} from "@components/AppContext";

import Header from "./Header";
import Footer from "./Footer";
import ConfirmSignUpHeader from "./ConfirmSignUpHeader";
import ConfirmSignUpFooter from "./ConfirmSignUpFooter";
import ConfirmSignInHeader from "./ConfirmSignInHeader";
import ConfirmSignInFooter from "./ConfirmSignInFooter";
import ResetPasswordHeader from "./ResetPasswordHeader";
import ResetPasswordConfirmHeader from "./ResetPasswordConfirmHeader";
import ResetPasswordConfirmFooter from "./ResetPasswordConfirmFooter";
import PasswordRequirements from "./PasswordRequirements";
import getFormFields from "./helpers/getFormFields";
import getQueryParams from "./helpers/getQueryParams";
import validatePassword from "./helpers/validatePassword";
import type { PasswordFormData } from "./helpers/validatePassword";

import "@aws-amplify/ui-react/styles.css";
import "./AuthenticatorContext.css";

type PublicOrganization = {
  name?: string;
  logoUrl?: string;
};

type Props = {
  children: ReactNode;
};

const PLATFORM_EMAIL = getConfig<string>("PLATFORM_EMAIL");
const AUTHENTICATOR_CONFIG = getConfig<Parameters<typeof Amplify.configure>[0]>(
  "AUTHENTICATOR_CONFIG",
);
Amplify.configure(AUTHENTICATOR_CONFIG);

const LABEL_HELLO = "Hello world!";
const LABEL_LEGACY_BROWSER = `You're using an outdated browser version. Please
update to the latest version or use another browser, like latest Chrome, or
Firefox. If you have questions contact`;

// eslint-disable-next-line prefer-regex-literals
const PUBLIC_REGEX = new RegExp("^/scf/(thank-you|create/[^/]+/[^/]+)$");

/**
 * Wraps its children with the Amplify Authenticator, its custom header,
 * footer and password rules, and skips sign-in for a public or an internal
 * route.
 */
const AuthenticatorContext = ({
  children,
}: Props): JSX.Element => {
  const publicOrganization = usePublicOrganization() as PublicOrganization;

  const { name, logoUrl } = publicOrganization;

  const isPublicScope = PUBLIC_REGEX.test(window.location.pathname);

  const { email, action } = getQueryParams();
  const initialState = action as AuthenticatorProps["initialState"];

  const components: AuthenticatorProps["components"] = {
    /**
     * Renders the organization's header for the Authenticator.
     */
    Header: () => Header(name, logoUrl),
    /**
     * Renders the organization's footer for the Authenticator.
     */
    Footer: () => Footer(),
    ResetPassword: {
      Header: ResetPasswordHeader,
    },
    ConfirmSignIn: {
      Header: ConfirmSignInHeader,
      Footer: ConfirmSignInFooter,
    },
    ConfirmSignUp: {
      Header: ConfirmSignUpHeader,
      Footer: ConfirmSignUpFooter,
    },
    ConfirmResetPassword: {
      Header: ResetPasswordConfirmHeader,
      Footer: ResetPasswordConfirmFooter,
    },
    SignUp: {
      /**
       * Renders the default sign-up fields plus the password rules.
       */
      FormFields() {
        return (
          <>
            <Authenticator.SignUp.FormFields />
            <PasswordRequirements />
          </>
        );
      },
    },
  };

  const isLegacyBrowser = !Object.hasOwn;

  if (isLegacyBrowser) {
    return (
      <div style={{ margin: "4px 6px", fontSize: 14 }}>
        {LABEL_LEGACY_BROWSER} <a href={`mailto:${PLATFORM_EMAIL}`}>{PLATFORM_EMAIL}</a>.
      </div>
    );
  }

  const isInternal = isInternalDomain();

  if (isInternal) {
    return (
      <div style={{ margin: "4px 6px", fontSize: 14 }}>
        {LABEL_HELLO}
      </div>
    );
  }

  const formFields = getFormFields(email);
  const loginMechanisms = [ "email" ] as AuthenticatorProps["loginMechanisms"];

  if (isPublicScope) {
    return (
      <PublicOrganizationContext.Provider value={{ publicOrganization }}>
        {children}
      </PublicOrganizationContext.Provider>
    );
  }

  const services = {
    /**
     * Validates the sign-up password against the platform's password rules.
     */
    async validateCustomSignUp(formData: PasswordFormData) {
      const errors = validatePassword(formData);

      return errors;
    },
  };

  return (
    <Authenticator
      components={components}
      services={services}
      formFields={formFields}
      initialState={initialState}
      loginMechanisms={loginMechanisms}
      /* td: Enable after backend is ready */
      // socialProviders={["google"]}
    >
      {
        () => (
          <PublicOrganizationContext.Provider value={{ publicOrganization }}>
            {children}
          </PublicOrganizationContext.Provider>
        )
      }
    </Authenticator>
  );
};

export default AuthenticatorContext;
